import { createContext, useCallback, useContext, useState } from 'react';
import { Lesson, RecommendedLesson, DataRefreshMode } from '../types';
import { useRecommendedLesson } from '../hooks/useRecommendedLesson';
import { useVisibleLessons } from '../hooks/useVisibleLessons';

type LessonsQueueDataContextType = {
  recommendedLesson: RecommendedLesson | null;
  upcomingLessons: Lesson[];
  handleSkip: () => void;
  handleDataRefresh: (mode: DataRefreshMode) => void;
  isLoading: boolean;
  isError: boolean;
  isSkipError: boolean;
  enabled: boolean;
  handleEnable: () => void;
};

const LessonsQueueDataContext = createContext<LessonsQueueDataContextType | null>(null);

export const LessonsQueueDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState(false);
  const handleEnable = useCallback(() => setEnabled(true), [setEnabled]);

  const {
    lessons,
    handleSkip,
    handleDataRefresh,
    isLoading: isLoadingLessons,
    isError: isErrorLessons,
    isSkipError,
  } = useVisibleLessons({ enabled });

  const [recommendedLessonBase, ...upcomingLessons] = lessons || [];

  const {
    recommendedLesson,
    isLoading: isLoadingRecommendedLesson,
    isError: isErrorRecommendedLesson,
  } = useRecommendedLesson(recommendedLessonBase);

  const value = {
    recommendedLesson,
    upcomingLessons,
    handleSkip,
    handleDataRefresh,
    isLoading: isLoadingLessons || isLoadingRecommendedLesson,
    isError: isErrorLessons || isErrorRecommendedLesson,
    isSkipError,
    enabled,
    handleEnable,
  };

  return (
    <LessonsQueueDataContext.Provider value={value}>{children}</LessonsQueueDataContext.Provider>
  );
};

export const useLessonsQueueData = () => {
  const context = useContext(LessonsQueueDataContext);
  if (!context) {
    throw new Error('useLessonsQueueData must be used within a LessonsQueueDataProvider');
  }
  return context;
};
